import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api";
import recommandedIcon from "../assets/icons/reccomnadedIocn.png";
import popularIcon from "../assets/icons/popularIocn.png";
import "../css/EnterpriseDetails.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CustomDateInput } from "../utils/formatDate";

import { toast } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import EnterpriseTenantKeySection from "../components/EnterpriseTenantKeySection";

const API_BASE = process.env.REACT_APP_API_URL;

const EnterpriseSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const enterprise = location.state?.enterprise;

  const [showSubscription, setShowSubscription] = useState(true);
  const [showOrganization, setShowOrganization] = useState(true);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [showModelSettings, setShowModelSettings] = useState(true);
  const [existingModelSettings, setExistingModelSettings] = useState([]);
  const [isModelSettingsFetched, setIsModelSettingsFetched] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(true);
  const [documentation, setDocumentation] = useState({
    auto_approve: false,
  });
  const [hasDocId, setHasDocId] = useState(false); // For backend check

  const [modelSettings, setModelSettings] = useState({
    recommended_models: [],
    popular_models: [],
  });

  const [availableModels, setAvailableModels] = useState([]);

  useEffect(() => {
    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    if (!token) return;

    const fetchModels = async () => {
      try {
        const res = await api.get(`${API_BASE}/models1`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const filtered = res.data.filter(
          (model) =>
            model.model_status === "Approved" &&
            model.model_download_status === "1" &&
            model.server_status === true, // ✅ added filter
        );

        setAvailableModels(filtered);
      } catch (err) {
        console.error("Error fetching models", err);
        showErrorToast(TOAST_MESSAGES.MODEL.FETCH_ERROR);
      }
    };

    fetchModels();
  }, []);

  // ✅ Fetch existing model settings
  useEffect(() => {
    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    if (!token) return;

    const id = enterprise?.enterprise_id;
    if (!id) return;

    api
      .get(`${API_BASE}/enterprise-model-settings/byEnterprise/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setExistingModelSettings(res.data || []);
        setIsModelSettingsFetched(true); // ✅ Mark fetched
      })
      .catch((err) => {
        console.error("Fetch enterprise model settings failed", err);
      });
  }, [enterprise]);

  // ✅ Combine only after both APIs are ready
  useEffect(() => {
    if (!availableModels.length || !isModelSettingsFetched) return;

    const recommendedModelsFromDB = existingModelSettings
      .filter((item) => item.enterprise_model_setting_status === "1")
      .map((item) => {
        const matchedModel = availableModels.find(
          (model) => model.model_id === item.model_fid,
        );
        return matchedModel?.model_name;
      })
      .filter(Boolean); // remove undefined

    setModelSettings((prev) => ({
      ...prev,
      recommended_models: recommendedModelsFromDB,
    }));
  }, [availableModels, existingModelSettings, isModelSettingsFetched]);

  const validateForm = () => {
    // --- Subscription Validation ---
    if (showSubscription) {
      if (!subscription.enterprise_subscription_name) {
        showErrorToast("Subscription Name is required");
        return false;
      }
      if (!subscription.enterprise_subscription_start_date) {
        showErrorToast("Subscription Start Date is required");
        return false;
      }
      if (!subscription.enterprise_subscription_end_date) {
        showErrorToast("Subscription End Date is required");
        return false;
      }
    }

    // --- Disclaimer Validation ---
    // if (showDisclaimer) {
    //   if (!disclaimer.enterprise_disclaimer_title) {
    //     showErrorToast("Disclaimer Title is required");
    //     return false;
    //   }
    // }

    return true; // ✅ all checks passed
  };

  const [subscription, setSubscription] = useState({
    enterprise_subscription_name: "",
    enterprise_subscription_duration: "",
    enterprise_subscription_prize: "",
    enterprise_subscription_start_date: "",
    enterprise_subscription_end_date: "",
  });

  const [organization, setOrganization] = useState({
    enterprise_organisation_settings_theme: "",
    can_admin_view_chat: "",
    can_power_user_view_chat: "",
    data_retention_period: "",
    allowed_doc_size_mb: "",
    training_frequency: "",
    enterprise_organisation_settings_selected_model: "",
  });

  const [disclaimer, setDisclaimer] = useState({
    enterprise_disclaimer_title: "",
    enterprise_disclaimer_display_location: "",
    enterprise_disclaimer_description: "",
  });

  const [hasSubId, setHasSubId] = useState(false);
  const [hasOrgId, setHasOrgId] = useState(false);
  const [hasDisId, setHasDisId] = useState(false);

  const handleInput = (setter, key) => (e) =>
    setter((prev) => ({ ...prev, [key]: e.target.value }));

  useEffect(() => {
    const id = enterprise?.enterprise_id;
    if (!id) return;

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    const headers = { Authorization: `Bearer ${token}` };

    api
      .get(`${API_BASE}/enterprise-subscriptions/byEnterprise/${id}`, {
        headers,
      })
      .then((res) => {
        if (res.data) {
          setHasSubId(true);
          setSubscription(res.data);
        }
      })
      .catch((err) => {
        console.error("Fetch subscription failed", err);
      });

    api
      .get(`${API_BASE}/enterprise-organisation-settings/byEnterprise/${id}`, {
        headers,
      })
      .then((res) => {
        if (res.data) {
          setHasOrgId(true);
          setOrganization(res.data);
        }
      })
      .catch((err) => {
        console.error("Fetch organization settings failed", err);
      });

    // api
    //   .get(`${API_BASE}/enterprise-disclaimer/byEnterprise/${id}`, {
    //     headers,
    //   })
    //   .then((res) => {
    //     if (res.data) {
    //       setHasDisId(true);
    //       setDisclaimer(res.data);
    //     }
    //   })
    //   .catch((err) => {
    //     console.error("Fetch disclaimer failed", err);
    //   });

    api
      .get(`${API_BASE}/enterprise-document-settings/byEnterprise/${id}`, {
        headers,
      })
      .then((res) => {
        if (res.data) {
          setHasDocId(true);
          setDocumentation(res.data);
        }
      })
      .catch((err) => {
        console.error("Fetch documentation settings failed", err);
      });
  }, [enterprise]);

  const handleSave = async () => {
    if (!validateForm()) return; // ⛔ Stop if validation fails

    const id = enterprise?.enterprise_id;
    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      // === Subscription ===
      if (hasSubId) {
        await api.put(
          `${API_BASE}/enterprise-subscriptions/byEnterprise/${id}`,
          { ...subscription, enterprise_fid: id },
          { headers },
        );
      } else {
        await api.post(
          `${API_BASE}/enterprise-subscriptions`,
          { ...subscription, enterprise_fid: id },
          { headers },
        );
      }

      // === Organization ===
      if (hasOrgId) {
        await api.put(
          `${API_BASE}/enterprise-organisation-settings/byEnterprise/${id}`,
          { ...organization, enterprise_fid: id },
          { headers },
        );
      } else {
        await api.post(
          `${API_BASE}/enterprise-organisation-settings`,
          { ...organization, enterprise_fid: id },
          { headers },
        );
      }

      // === Documentation Settings ===
      // if (hasDocId) {
      //   await api.put(
      //     `${API_BASE}/enterprise-document-settings/byEnterprise/${id}`,
      //     { ...documentation, enterprise_fid: id },
      //     { headers }
      //   );
      // } else {
      //   await api.post(
      //     `${API_BASE}/enterprise-document-settings`,
      //     { ...documentation, enterprise_fid: id },
      //     { headers }
      //   );
      // }

      // // === Disclaimer ===
      // if (hasDisId) {
      //   await api.put(
      //     `${API_BASE}/enterprise-disclaimer/byEnterprise/${id}`,
      //     { ...disclaimer, enterprise_fid: id },
      //     { headers }
      //   );
      // } else {
      //   await api.post(
      //     `${API_BASE}/enterprise-disclaimer`,
      //     { ...disclaimer, enterprise_fid: id },
      //     { headers }
      //   );
      // }

      // === Unified success message ===

      showSuccessToast(TOAST_MESSAGES.ENTERPRISE_SETTINGS.SAVE_SUCCESS);
      setTimeout(() => navigate(-1), 1200); // short delay for visibility
    } catch (err) {
      console.error("Save failed:", err);
      showErrorToast(TOAST_MESSAGES.ENTERPRISE_SETTINGS.SAVE_ERROR);
    }

    const modelSettingPayloads = [];

    // 1. Track processed model IDs to avoid duplication
    const processedModelIds = new Set();

    // 2. Create or update selected models (status: "1")
    modelSettings.recommended_models.forEach((modelName) => {
      const foundModel = availableModels.find(
        (m) => m.model_name === modelName,
      );
      if (!foundModel) return;

      processedModelIds.add(foundModel.model_id);

      const existing = existingModelSettings.find(
        (ems) => ems.model_fid === foundModel.model_id,
      );

      if (existing) {
        // Update to status "1"
        modelSettingPayloads.push(
          api.put(
            `${API_BASE}/enterprise-model-settings/byEnterprise/${id}`,
            {
              model_fid: foundModel.model_id,
              enterprise_model_setting_status: "1",
            },
            { headers },
          ),
        );
      } else {
        // Create new
        modelSettingPayloads.push(
          api.post(
            `${API_BASE}/enterprise-model-settings`,
            {
              enterprise_fid: id,
              model_fid: foundModel.model_id,
              enterprise_model_setting_status: "1",
            },
            { headers },
          ),
        );
      }
    });

    // 3. Deactivate models that were previously saved but now unchecked (status: "0")
    existingModelSettings.forEach((existing) => {
      if (!processedModelIds.has(existing.model_fid)) {
        modelSettingPayloads.push(
          api.put(
            `${API_BASE}/enterprise-model-settings/byEnterprise/${id}`,
            {
              model_fid: existing.model_fid,
              enterprise_model_setting_status: "0",
            },
            { headers },
          ),
        );
      }
    });

    await Promise.all(modelSettingPayloads);
  };

  return (
    <div className="py-2 px-3 h-100 overflow-y-auto">
      <div className="">
        {/* === Subscription === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowSubscription(!showSubscription)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">1</div>
            <h5 className="section-title mb-0">Subscription Details</h5>
          </div>
          <i
            className={`bi ${
              showSubscription ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>
        {showSubscription && (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <label>
                  Subscription Name
                  <sup className="text-danger ms-2 fw-bolder">*</sup>
                </label>
                <select
                  className="form-select"
                  value={subscription.enterprise_subscription_name}
                  onChange={handleInput(
                    setSubscription,
                    "enterprise_subscription_name",
                  )}
                  required
                >
                  <option value="">Select Subscription</option>
                  <option value="basic">Basic</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Subscription Duration</label>
                <input
                  type="text"
                  className="form-control"
                  value={subscription.enterprise_subscription_duration}
                  onChange={handleInput(
                    setSubscription,
                    "enterprise_subscription_duration",
                  )}
                />
              </div>
              <div className="col-md-4">
                <label>Subscription Price</label>
                <input
                  type="text"
                  className="form-control"
                  value={subscription.enterprise_subscription_prize}
                  onChange={handleInput(
                    setSubscription,
                    "enterprise_subscription_prize",
                  )}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4">
                <label>
                  Subscription Start Date{" "}
                  <sup className="text-danger ms-2 fw-bolder">*</sup>
                </label>
                <DatePicker
                  className="datecolor"
                  calendarClassName="datecolor" // calendar popup
                  selected={
                    subscription.enterprise_subscription_start_date
                      ? new Date(
                          subscription.enterprise_subscription_start_date,
                        )
                      : null
                  }
                  onChange={(date) => {
                    const iso = date.toLocaleDateString("en-CA"); // ✅ local YYYY-MM-DD
                    setSubscription((prev) => ({
                      ...prev,
                      enterprise_subscription_start_date: iso,
                      enterprise_subscription_end_date:
                        prev.enterprise_subscription_end_date < iso
                          ? ""
                          : prev.enterprise_subscription_end_date,
                    }));
                  }}
                  dateFormat="MM/dd/yyyy"
                  minDate={new Date()}
                  customInput={<CustomDateInput required />}
                />
              </div>

              <div className="col-md-4">
                <label>
                  Subscription End Date{" "}
                  <sup className="text-danger ms-2 fw-bolder">*</sup>
                </label>
                <DatePicker
                  selected={
                    subscription.enterprise_subscription_end_date
                      ? new Date(subscription.enterprise_subscription_end_date)
                      : null
                  }
                  onChange={(date) => {
                    const iso = date.toLocaleDateString("en-CA"); // ✅ local YYYY-MM-DD
                    setSubscription((prev) => ({
                      ...prev,
                      enterprise_subscription_end_date: iso,
                    }));
                  }}
                  dateFormat="MM/dd/yyyy"
                  minDate={
                    subscription.enterprise_subscription_start_date
                      ? new Date(
                          subscription.enterprise_subscription_start_date,
                        )
                      : new Date()
                  }
                  customInput={<CustomDateInput required />}
                />
              </div>
            </div>
          </>
        )}

        {/* === Organization === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowOrganization(!showOrganization)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">2</div>
            <h5 className="section-title mb-0">Organization Settings</h5>
          </div>
          <i
            className={`bi ${
              showOrganization ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>
        {showOrganization && (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <label>Theme</label>
                <select
                  className="form-select"
                  value={organization.enterprise_organisation_settings_theme}
                  onChange={handleInput(
                    setOrganization,
                    "enterprise_organisation_settings_theme",
                  )}
                >
                  <option value="">Select Theme</option>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Can Admin View Chat</label>
                <select
                  className="form-select"
                  value={organization.can_admin_view_chat}
                  onChange={handleInput(setOrganization, "can_admin_view_chat")}
                >
                  <option value="">Select Option</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>
              {/* <div className="col-md-4">
                <label>Can SuperAdmin View Chat</label>
                <select
                  className="form-select"
                  value={organization.can_power_user_view_chat}
                  onChange={handleInput(
                    setOrganization,
                    "can_power_user_view_chat"
                  )}
                >
                  <option value="">Select Option</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div> */}

              <div className="col-md-4">
                <label>Select Model</label>
                <select
                  className="form-select"
                  value={
                    organization.enterprise_organisation_settings_selected_model
                  }
                  onChange={handleInput(
                    setOrganization,
                    "enterprise_organisation_settings_selected_model",
                  )}
                >
                  <option value="">Choose Model</option>
                  <option value="gpt-3.5">GPT-3.5</option>
                  <option value="gpt-4">GPT-4</option>
                  <option value="custom">Custom Model</option>
                </select>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <label>No need to store data</label>
                <select
                  className="form-select"
                  value={organization.data_retention_period}
                  onChange={handleInput(
                    setOrganization,
                    "data_retention_period",
                  )}
                >
                  <option value="">Select Duration</option>
                  <option value="30">30 Days</option>
                  <option value="90">90 Days</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Total Allowed Document Size</label>
                <select
                  className="form-select"
                  value={organization.allowed_doc_size_mb}
                  onChange={handleInput(setOrganization, "allowed_doc_size_mb")}
                >
                  <option value="">Select Size</option>
                  <option value="100">100 MB</option>
                  <option value="500">500 MB</option>
                  <option value="1024">1 GB</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Training Frequency</label>
                <select
                  className="form-select"
                  value={organization.training_frequency}
                  onChange={handleInput(setOrganization, "training_frequency")}
                >
                  <option value="">Select Frequency</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>
          </>
        )}

        {/* === Disclaimer === */}
        {/* <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowDisclaimer(!showDisclaimer)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">3</div>
            <h5 className="section-title mb-0">Disclaimer Settings</h5>
          </div>
          <i
            className={`bi ${
              showDisclaimer ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>
        {showDisclaimer && (
          <>
            <div className="row mb-3">
              <div className="col-md-6">
                <label>
                  Disclaimer Title
                  <sup className="text-danger ms-2 fw-bolder">*</sup>
                </label>
                <input
                  className="form-control"
                  value={disclaimer.enterprise_disclaimer_title}
                  onChange={handleInput(
                    setDisclaimer,
                    "enterprise_disclaimer_title"
                  )}
                  required
                />
              </div>
              <div className="col-md-6">
                <label>Display Location</label>
                <select
                  className="form-select"
                  value={disclaimer.enterprise_disclaimer_display_location}
                  onChange={handleInput(
                    setDisclaimer,
                    "enterprise_disclaimer_display_location"
                  )}
                >
                  <option value="">Select Location</option>
                  <option value="top">Top</option>
                  <option value="bottom">Bottom</option>
                  <option value="popup">Popup</option>
                </select>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-12">
                <label>Disclaimer Description</label>
                <textarea
                  className="input-textarea1 p-2"
                  rows="4"
                  value={disclaimer.enterprise_disclaimer_description}
                  onChange={handleInput(
                    setDisclaimer,
                    "enterprise_disclaimer_description"
                  )}
                />
              </div>
            </div>
          </>
        )} */}

        {/* === Model Settings === */}
        <EnterpriseTenantKeySection
          enterpriseId={enterprise?.enterprise_id}
          sectionNumber={2}
          defaultOpen={true}
        />

        <div
          className="section-header d-flex align-items-center gap-2 mb-3"
          style={{ cursor: "pointer" }}
          onClick={() => setShowModelSettings(!showModelSettings)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">3</div>
            <h5 className="section-title mb-0">Model Settings</h5>
          </div>
          <i
            className={`bi ${
              showModelSettings ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>

        {showModelSettings && (
          <>
            {/* === Recommended Models === */}
            <div className="mb-3">
              <h6 className="d-flex align-items-center">
                <img
                  src={recommandedIcon}
                  alt="Recommended Icon"
                  style={{ width: "25px", height: "28px" }}
                  className="me-2"
                />
                Recommended Models
              </h6>
              <div className="mx-2">
                {/* ================= OFFLINE MODELS ================= */}
                <div className="fw-bold refresh-prompts mb-2">
                  OFFLINE MODELS
                </div>

                {availableModels.filter((m) => m.model_type === "Offline")
                  .length === 0 ? (
                  <div className="text-muted fst-italic mb-3">
                    No offline models found. Please add or download an offline
                    model to continue.
                  </div>
                ) : (
                  <div className="row mb-3">
                    {availableModels
                      .filter((model) => model.model_type === "Offline")
                      .map((model, index) => {
                        const isChecked =
                          modelSettings.recommended_models.includes(
                            model.model_name,
                          );

                        return (
                          <div
                            className="col-md-3 mb-2"
                            key={`offline-${index}`}
                          >
                            <div className="form-check">
                              <input
                                className="custom-checkbox"
                                type="checkbox"
                                id={`offline-${index}`}
                                checked={isChecked}
                                onChange={(e) => {
                                  const updated = e.target.checked
                                    ? [
                                        ...modelSettings.recommended_models,
                                        model.model_name,
                                      ]
                                    : modelSettings.recommended_models.filter(
                                        (m) => m !== model.model_name,
                                      );

                                  setModelSettings((prev) => ({
                                    ...prev,
                                    recommended_models: updated,
                                  }));
                                }}
                              />
                              <label className="ms-2 form-check-label">
                                {model.model_generic_name}
                              </label>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}

                {/* ================= ONLINE MODELS ================= */}
                <div className="fw-bold mb-2 refresh-prompts">
                  ONLINE MODELS
                </div>

                {availableModels.filter((m) => m.model_type === "Online")
                  .length === 0 ? (
                  <div className="text-muted fst-italic mb-3">
                    No online models found. Please configure or enable an online
                    model to continue.
                  </div>
                ) : (
                  [
                    ...new Set(
                      availableModels
                        .filter((m) => m.model_type === "Online")
                        .map((m) => m.model_provider || "unknown"),
                    ),
                  ]
                    .sort((a, b) => {
                      if (a === "unknown") return 1;
                      if (b === "unknown") return -1;
                      return a.localeCompare(b);
                    })
                    .map((provider) => (
                      <div
                        className="row align-items-center mb-2"
                        key={provider}
                      >
                        {/* Provider Name */}
                        <div className="col-md-2 fw-bold refresh-prompts">
                          {provider.toUpperCase()}
                        </div>

                        {/* Provider Models */}
                        <div className="col-md-10 d-flex flex-wrap">
                          {availableModels
                            .filter(
                              (m) =>
                                m.model_type === "Online" &&
                                (m.model_provider || "unknown") === provider,
                            )
                            .map((model, index) => {
                              const isChecked =
                                modelSettings.recommended_models.includes(
                                  model.model_name,
                                );

                              return (
                                <div
                                  className="form-check me-4"
                                  key={`${provider}-${index}`}
                                >
                                  <input
                                    className="custom-checkbox"
                                    type="checkbox"
                                    id={`${provider}-${index}`}
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const updated = e.target.checked
                                        ? [
                                            ...modelSettings.recommended_models,
                                            model.model_name,
                                          ]
                                        : modelSettings.recommended_models.filter(
                                            (m) => m !== model.model_name,
                                          );

                                      setModelSettings((prev) => ({
                                        ...prev,
                                        recommended_models: updated,
                                      }));
                                    }}
                                  />
                                  <label
                                    className="ms-2 form-check-label"
                                    htmlFor={`${provider}-${index}`}
                                  >
                                    {model.model_generic_name}
                                  </label>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>

            {/* === Popular Models === */}
            {/* <div className="mb-4">
              <h6 className="d-flex align-items-center">
                <img
                  src={popularIcon}
                  alt="Popular Icon"
                  style={{ width: "25px", height: "25px" }}
                  className="me-2"
                />
                Popular Models
              </h6>
              <div className="row mx-2">
                {[
                  "Mistral 7B",
                  "Claude 3",
                  "GPT-4",
                  "Gemini Pro",
                  "LLaMA 2",
                  "Command R",
                  "GPT-3",
                  "Falcon 180B",
                ].map((model, index) => (
                  <div className="col-md-3 mb-2" key={`pop-model-${index}`}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`pop-model-${index}`}
                        checked={modelSettings.popular_models.includes(model)}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...modelSettings.popular_models, model]
                            : modelSettings.popular_models.filter(
                                (m) => m !== model
                              );
                          setModelSettings((prev) => ({
                            ...prev,
                            popular_models: updated,
                          }));
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`pop-model-${index}`}
                      >
                        {model}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div> */}
          </>
        )}

        {/* === Documentation Settings === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowDocumentation(!showDocumentation)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">4</div>
            <h5 className="section-title mb-0">Documentation Settings</h5>
          </div>
          <i
            className={`bi ${
              showDocumentation ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>

        {showDocumentation && (
          <div className="row mb-4">
            <div className="col-md-4 d-flex align-items-center">
              <label className="me-3 mb-0">Auto Approve</label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={documentation.auto_approve || false}
                  onChange={(e) =>
                    setDocumentation((prev) => ({
                      ...prev,
                      auto_approve: e.target.checked,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        )}

        <hr className="my-4" />

        <div className="d-flex justify-content-center gap-2">
          <button
            type="button"
            className="cancle-btn"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
          <button type="button" className="custom-btn" onClick={handleSave}>
            Save
          </button>
        </div>
        {/* <hr className="mt-5" /> */}
      </div>
    </div>
  );
};

export default EnterpriseSettings;
